<template>
    <v-card>
        <v-card-title>
            <v-layout class="title">
                <v-btn disabled class="mr-7">
                    <v-icon left>keyboard_arrow_left</v-icon>
                    {{ $t('previous') }}
                </v-btn>

                {{ $i18n.t('question') + ' #' + getQuestion.id }}

                <v-spacer/>

                <v-btn @click="$store.commit('setEditingQuestion', false)">
                    {{ $t('cancel') }}
                    <v-icon right>clear</v-icon>
                </v-btn>
                <v-btn @click="submit" class="ml-7" color="teal">
                    {{ $t('save') }}
                    <v-icon right>done</v-icon>
                </v-btn>

                <v-btn disabled class="ml-7">
                    {{ $t('next') }}
                    <v-icon right>keyboard_arrow_right</v-icon>
                </v-btn>
            </v-layout>
        </v-card-title>

        <v-form ref="form" @submit="submit">
            <v-card-text>
                <v-container grid-list-xl>
                    <v-layout col>
                        <v-textarea dense auto-grow rows=1 v-model="text" :rules="textRules" :placeholder="getQuestion.text" :label="$i18n.t('text')" class="my-n4 headline">
                            <v-tooltip slot="append" v-if="text != getQuestion.text" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon v-on="on" @click="text = getQuestion.text">replay</v-icon>
                                </template>
                                <span>{{ $t('undo') }}</span>
                            </v-tooltip>
                        </v-textarea>
                    </v-layout>

                    <v-row align="center">
                        <v-col cols=7>
                            <v-radio-group v-model="correctAnswer">
                                <v-radio v-for="answer in answers" :key="answer.position">
                                    <template v-slot:label>
                                        {{ ['A','B','C','D','E','F','G','H','I','J'][answer.position-1] }}
                                        <v-textarea dense auto-grow rows=1 v-model="answer.text" :rules="answerRules" :placeholder="getQuestion.answers[answer.position-1].text" class="ml-12 body-1 font-weight-light">
                                            <v-tooltip slot="append" v-if="answer.text != getQuestion.answers[answer.position-1].text" bottom>
                                                <template v-slot:activator="{ on }">
                                                    <v-icon v-on="on" @click="answer.text = getQuestion.answers[answer.position-1].text">replay</v-icon>
                                                </template>
                                                <span>{{ $t('undo') }}</span>
                                            </v-tooltip>
                                        </v-textarea>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </v-col>

                        <v-col cols=5 order="last" align="center">
                            <v-row>
                                <v-col>
                                    <v-img v-if="getQuestionImage" :src="getQuestionImage" contain height="250px"/>
                                    <v-icon x-large v-else>photo_camera</v-icon>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-file-input @change="encode" outlined dense reverse rounded show-size accept="image/*,.nos" prepend-icon="" :placeholder="$i18n.t('editImage')" style="width: 220px"/>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>

                    <v-divider/>

                    <v-list>
                        <v-list-item>
                            <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('title') }}</v-flex>
                            <v-flex>
                                <v-text-field dense v-model="title" :rules="titleRules" :placeholder="getQuestion.title" :hint="$i18n.t('titleHint')" class="pa-0 ma-0">
                                    <v-tooltip slot="append" v-if="title != getQuestion.title" bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-icon v-on="on" @click="title = getQuestion.title">replay</v-icon>
                                        </template>
                                        <span>{{ $t('undo') }}</span>
                                    </v-tooltip>
                                </v-text-field>
                            </v-flex>
                        </v-list-item>
                        <v-list-item>
                            <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('observations') }}</v-flex>
                            <v-flex>
                                <v-text-field dense v-model="notes" :rules="notesRules" :placeholder="getQuestion.notes" :hint="$i18n.t('notesHint')" class="mt-2">
                                    <v-tooltip slot="append" v-if="notes != getQuestion.notes" bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-icon v-on="on" @click="notes = getQuestion.notes">replay</v-icon>
                                        </template>
                                        <span>{{ $t('undo') }}</span>
                                    </v-tooltip>
                                </v-text-field>
                            </v-flex>
                        </v-list-item>
                    </v-list>

                    <v-divider v-if="!!getQuestion.difficulty && !!getQuestion.discrimination"/>

                    <v-list v-if="!!getQuestion.difficulty && !!getQuestion.discrimination">
                        <v-list-item>
                            <v-flex xs3 class="subheading font-weight-medium">
                                <v-tooltip open-on-hover bottom>
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            {{ $i18n.t('discrimination') }}
                                        </div>
                                    </template>
                                    <span>{{ $i18n.t('discriminationTooltip') }}</span>
                                </v-tooltip>
                            </v-flex>

                            <v-flex class="subheading font-weight-light">{{ (getQuestion.discrimination).toFixed(2) }}</v-flex>
                        </v-list-item>
                        <v-list-item>
                            <v-flex xs3 class="subheading font-weight-medium">
                                <v-tooltip open-on-hover bottom>
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            {{ $i18n.t('difficulty') }}
                                        </div>
                                    </template>
                                    <span>{{ $i18n.t('difficultyTooltip') }}</span>
                                </v-tooltip>
                            </v-flex>

                            <v-flex class="subheading font-weight-light">{{ (getQuestion.difficulty).toFixed(2) }}</v-flex>
                        </v-list-item>
                    </v-list>

                    <v-divider/>

                    <v-list>
                        <v-list-item class="mt-n3">
                            <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('topic') }}</v-flex>
                            <v-select :items="getTopics.map(topic => topic.position + '. ' + topic.name)" v-model="topic" style="width: 70px"/>
                        </v-list-item>
                        <v-list-item class="mt-n3">
                            <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('advanced') }}</v-flex>
                            <v-radio-group v-model="basic" row>
                                <v-radio :label="$i18n.t('yes')"/>
                                <v-radio :label="$i18n.t('no')"/>
                            </v-radio-group>
                        </v-list-item>
                        <v-list-item>
                            <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('modificationDate') }}</v-flex>
                        <v-flex class="subheading font-weight-light" v-if="getQuestion.lastUpdatedBy == 1">{{ toDate(getQuestion.lastUpdatedAt) }}</v-flex>
                        <v-flex class="subheading font-weight-light" v-else>{{ toDate(getQuestion.lastUpdatedAt) + " " + $i18n.t('by') + " " + getQuestion.lastUpdatedByName }}</v-flex>
                        </v-list-item>
                        <v-list-item>
                            <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('creationDate') }}</v-flex>
                            <v-flex class="subheading font-weight-light" v-if="getQuestion.createdBy == 1">{{ toDate(getQuestion.createdAt) }}</v-flex>
                            <v-flex class="subheading font-weight-light" v-else>{{ toDate(getQuestion.createdAt) + " " + $i18n.t('by') + " " + getQuestion.createdByName }}</v-flex>
                        </v-list-item>
                    </v-list>

                    <v-divider/>

                    <v-list>
                        <v-list-item>
                            <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('testsAppearances') }}</v-flex>
                            <v-flex class="subheading font-weight-light">{{ formulations.length }}</v-flex>
                        </v-list-item>
                        <v-list-item v-if="formulations.length > 0">
                            <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('lastAppearance') }}</v-flex>
                            <v-flex class="subheading font-weight-light">{{ toDate(formulations.map(formulation => formulation.test.testDate).sort().reverse()[0]) }}</v-flex>
                        </v-list-item>
                    </v-list>


                    <v-simple-table class="ml-7" v-if="formulations.length > 0">
                        <template v-slot:default>
                            <thead>
                                <tr>
                                    <th class="text-left">{{ $i18n.t('title') }}</th>
                                    <th class="text-left">{{ $i18n.t('testDate') }}</th>
                                    <th class="text-center">{{ $i18n.t('questionDiscrimination') }}</th>
                                    <th class="text-center">{{ $i18n.t('questionDifficulty') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="formulation in formulations" :key="formulation.id">
                                    <td>{{ formulation.test.title }}</td>
                                    <td>{{ toDate(formulation.test.testDate) }}</td>
                                    <td class="text-center">{{ formulation.discrimination }}</td>
                                    <td class="text-center">{{ formulation.difficulty }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                </v-container>
            </v-card-text>
        </v-form>
    </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { i18n } from '@/main.js' 
import toDate from '@/components/dateParser';

export default {
    data(){
        return {
            reader: new FileReader(),
            titleRules: [
                v => !v || v.length <= 1000 || i18n.t('longTitle'),
            ],
            textRules: [
                v => !!v || i18n.t('noText'),
                v => !v || v.length <= 1000 || i18n.t('longText'),
            ],
            answerRules: [
                v => !v || v.length <= 255 || i18n.t('longAnswer'),
            ],
            notesRules: [
                v => !v || v.length <= 1000 || i18n.t('longNotes'),
            ],
        }
    },

    created() {
        this.reader.onloadend = () => { this.image = this.reader.result }
    },

    methods: {
        submit() {
            if (this.$refs.form.validate())
                this.$store.dispatch('updateQuestion')
        },
        encode(file) {
            if (!!file){
                this.reader.readAsDataURL(file)
            } else {
                this.image = this.getQuestion.imageString
            }
        }
    },

    computed:{
        ...mapGetters([
            'isLoading',
            'getQuestion',
            'getFormulationsByQuestion',
            'getTopics',
            'getQuestionTitle',
            'getQuestionText',
            'getQuestionAnswers',
            'getQuestionCorrect',
            'getQuestionNotes',
            'getQuestionImage',
            'getQuestionBasic',
            'getQuestionTopicId',
        ]),
        title: {
            get() { return this.getQuestionTitle },
            set(value) { this.$store.commit('setQuestionTitle', value) },
        },
        text: {
            get() { return this.getQuestionText },
            set(value) { this.$store.commit('setQuestionText', value) },
        },
        answers: {
            get() { return this.getQuestionAnswers },
            set(value) { this.$store.commit('setQuestionAnswers', value) },
        },
        correctAnswer: {
            get() { return this.getQuestionCorrect-1 },
            set(value) { this.$store.commit('setQuestionCorrect', value+1) },
        },
        notes: {
            get() { return this.getQuestionNotes },
            set(value) { this.$store.commit('setQuestionNotes', value ) },
        },
        image: {
            get() { return this.getQuestionImage },
            set(value) { this.$store.commit('setQuestionImage', value ) },
        },
        basic: {
            get() { return this.getQuestionBasic ? 1 : 0 },
            set(value) { this.$store.commit('setQuestionBasic', value ? true : false) },
        },
        topic: {
            get() {
                var topic = this.getTopics.find(topic => topic.id == this.getQuestionTopicId)
                return topic.position + ". " + topic.name
            },
            set(value) {
                var position = value.substr(0, value.indexOf('.'))
                var topic = this.getTopics.find(topic => topic.position == position)
                this.$store.commit('setQuestionTopicId', topic.id)
            },
        },
        formulations(){
            return this.getFormulationsByQuestion.filter(formulation => formulation.test.testDate != null) // Remove draft exams
        },
    },

    mixins: [toDate],
}
</script>

<style>
    .v-textarea textarea {
        line-height: 30px;
    }
</style>
